import { action } from '@ember/object';
import { tracked, cached } from '@glimmer/tracking';
import { waitFor } from '@ember/test-waiters';
import { service } from '@ember/service';
import type { Registry as Services } from '@ember/service';
import BaseController from 'uplisting-frontend/pods/base/controller';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { searchOptions } from 'uplisting-frontend/utils/search';
import { FilterId } from 'uplisting-frontend/utils/filters/abstract';
import { animatedTransition } from 'uplisting-frontend/utils/router';
import {
  type Filter,
  type KnowledgeCenterFilter,
  getFilterQueryParamName,
} from 'uplisting-frontend/utils/filters/types';
import {
  KnowledgeBaseQaPairCategory,
  KnowledgeBaseQaPairStatus,
} from 'uplisting-frontend/models/ai-knowledge-base-qa-pair';

export default class AiMessagingKnowledgeCenterController extends BaseController {
  baseRoute = 'ai-messaging';
  @service sentry!: Services['sentry'];
  @service('filters') filtersService!: Services['filters'];
  @service('repositories/ai-knowledge-base-qa-pair')
  qaPairRepository!: Services['repositories/ai-knowledge-base-qa-pair'];

  queryParams = ['categories', 'statuses', 'propertyIds', 'highlightQaPairId'];

  @tracked isLoading = false;
  @tracked qaPairs: ModelRegistry['ai-knowledge-base-qa-pair'][] = [];
  @tracked properties: ModelRegistry['property'][] = [];
  @tracked searchQuery = '';
  @tracked categories: string[] = [];
  @tracked statuses: string[] = [];
  @tracked propertyIds: string[] = [];
  @tracked highlightQaPairId?: string;
  @tracked activeTab: 'global' | 'property' = 'global';
  @tracked isPanelOpen = false;
  @tracked editingQaPair?: ModelRegistry['ai-knowledge-base-qa-pair'];
  @tracked deletingQaPair?: ModelRegistry['ai-knowledge-base-qa-pair'];
  @tracked isFileUploadPanelOpen = false;

  @cached
  get filters(): KnowledgeCenterFilter[] {
    return [
      this.filtersService.createFilterFor(
        FilterId.knowledgeCenterCategory,
        this.categories,
      ),
      this.filtersService.createFilterFor(
        FilterId.knowledgeCenterStatus,
        this.statuses,
      ),
    ] as KnowledgeCenterFilter[];
  }

  @cached
  get propertyFilter(): KnowledgeCenterFilter {
    return this.filtersService.createFilterFor(
      FilterId.property,
      this.propertyIds,
    ) as KnowledgeCenterFilter;
  }

  @cached
  get displayedQaPairs(): ModelRegistry['ai-knowledge-base-qa-pair'][] {
    let pairs = this.qaPairs.filter((pair) => !pair.isNew);

    if (this.activeTab === 'global') {
      pairs = pairs.filter((pair) => !pair.belongsTo('property').id());
    } else {
      pairs = pairs.filter((pair) => !!pair.belongsTo('property').id());
    }

    return searchOptions(pairs, this.searchQuery, ['question', 'answer']);
  }

  @action
  handleSearchInput(event: Event): void {
    this.searchQuery = (event.target as HTMLInputElement).value;
  }

  @action
  async handleFilterUpdate(filter: Filter): Promise<void> {
    const queryParamField = getFilterQueryParamName(filter);

    this[queryParamField] = filter.toQuery();

    await this.fetchData();
  }

  @action
  async handleTabChange(tab: 'global' | 'property'): Promise<void> {
    this.activeTab = tab;
    this.propertyIds = [];

    await this.fetchData();
  }

  async fetchData(): Promise<void> {
    this.isLoading = true;

    try {
      const data = await this.qaPairRepository.query({
        categories: this.categories,
        statuses: this.statuses,
        property_ids: this.propertyIds,
      });

      this.qaPairs = data.slice();

      this.openHighlightedQaPair();
    } catch (error) {
      this.sentry.captureException(error);
      this.notifications.error();
    } finally {
      this.isLoading = false;
    }
  }

  openHighlightedQaPair(): void {
    if (!this.highlightQaPairId) return;

    const match = this.qaPairs.find(
      (pair) => pair.id === this.highlightQaPairId,
    );

    if (match) {
      this.handleOpenEditPanel(match);
    }

    this.highlightQaPairId = undefined;
  }

  @action
  handleOpenAddPanel(): void {
    const record = this.store.createRecord('ai-knowledge-base-qa-pair', {
      category: KnowledgeBaseQaPairCategory.other,
      status: KnowledgeBaseQaPairStatus.needsReview,
    });

    animatedTransition(() => {
      this.editingQaPair = record;
      this.isPanelOpen = true;
    });
  }

  @action
  handleOpenEditPanel(
    qaPair: ModelRegistry['ai-knowledge-base-qa-pair'],
  ): void {
    animatedTransition(() => {
      this.editingQaPair = qaPair;
      this.isPanelOpen = true;
    });
  }

  @action
  async handleClosePanel(didSave = false): Promise<void> {
    if (this.editingQaPair?.isNew) {
      this.editingQaPair.unloadRecord();
    }

    animatedTransition(() => {
      this.isPanelOpen = false;
      this.editingQaPair = undefined;
    });

    if (didSave) {
      await this.fetchData();
    }
  }

  @action
  @waitFor
  async handleStatusChange(
    qaPair: ModelRegistry['ai-knowledge-base-qa-pair'],
    status: KnowledgeBaseQaPairStatus,
  ): Promise<void> {
    qaPair.status = status;

    try {
      await qaPair.save();

      this.notifications.info('knowledge_center.notifications.saved');
    } catch (error) {
      this.sentry.captureException(error);
      qaPair.rollbackAttributes();
      this.notifications.error();
    }
  }

  @action
  handleDeleteQaPair(qaPair: ModelRegistry['ai-knowledge-base-qa-pair']): void {
    this.deletingQaPair = qaPair;
  }

  @action
  handleCancelDelete(): void {
    this.deletingQaPair = undefined;
  }

  @action
  @waitFor
  async handleConfirmDelete(): Promise<void> {
    const qaPair = this.deletingQaPair;

    if (!qaPair) return;

    this.deletingQaPair = undefined;

    try {
      await qaPair.destroyRecord();

      await this.fetchData();

      this.notifications.info('knowledge_center.notifications.deleted');
    } catch (error) {
      this.sentry.captureException(error);
      this.notifications.error();
    }
  }

  @action
  handleViewSource(qaPair: ModelRegistry['ai-knowledge-base-qa-pair']): void {
    const file = qaPair.knowledgeBaseFile;

    if (!file) return;

    const propertyId = qaPair.property?.id;

    this.router.transitionTo(`${this.baseRoute}.content-files`, {
      queryParams: {
        propertyIds: propertyId ? [propertyId] : [],
        highlightFileId: file.id,
      },
    });
  }

  @action
  openFileUploadPanel(): void {
    this.isFileUploadPanelOpen = true;
  }

  @action
  closeFileUploadPanel(): void {
    this.isFileUploadPanelOpen = false;
  }
}
